import { useState, useRef, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ContentRow as ContentRowType } from '@/types';
import Badge from '@/components/ui/Badge/Badge';
import { trackEvent } from '@/services/analyticsService';
import { ANALYTICS_EVENTS } from '@/config/analyticsEvents';
import styles from './ContentRow.module.css';

interface ContentRowProps {
  row: ContentRowType;
  children: ReactNode;
  /** Always render expanded regardless of collapse state — used for the
   * chip-filtered view and Image Intelligence mode, where the row being
   * shown should never be hidden. */
  forceExpanded?: boolean;
  loading?: boolean;
  onDelete?: () => void;
}

const SKELETON_COUNT = 6;

function SkeletonShelf() {
  return (
    <div className={styles.shelf} aria-busy="true" aria-label="Loading content">
      {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
        <div key={i} className={styles.skeletonTile}>
          <div className={styles.skeletonThumb} />
          <div className={styles.skeletonBody}>
            <div className={`${styles.skeletonLine} ${styles.skeletonLineWide}`} />
            <div className={`${styles.skeletonLine} ${styles.skeletonLineNarrow}`} />
            <div className={`${styles.skeletonLine} ${styles.skeletonLineXnarro}`} />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyShelf({ type }: { type: ContentRowType['type'] }) {
  const isPodcast = type === 'pod';
  return (
    <div className={styles.emptyShelf} role="status" aria-label="No content">
      {isPodcast ? (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="22" />
        </svg>
      ) : (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      )}
      <p className={styles.emptyTitle}>No content yet</p>
      <p className={styles.emptySub}>{isPodcast ? 'Podcast episodes will appear here once ingested.' : 'Check back soon — new content is indexed daily.'}</p>
    </div>
  );
}

export default function ContentRow({ row, children, forceExpanded = false, loading = false, onDelete }: ContentRowProps) {
  const navigate = useNavigate();
  // Collapse state is purely in-memory — every collection with content
  // starts OPEN on every mount (including a full page refresh); collapsing
  // one is a this-session-only choice, never persisted. A section with no
  // items still collapses automatically.
  const [collapsed, setCollapsed] = useState(() => !loading && row.items.length === 0);
  const hadItemsRef = useRef(row.items.length > 0);
  const prevLoadingRef = useRef(loading);

  // Auto-expand when content loads in to a previously empty section;
  // auto-collapse when loading finishes and section has no items.
  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = loading;

    if (!hadItemsRef.current && row.items.length > 0) {
      // Items just arrived — expand
      setCollapsed(false);
    } else if (wasLoading && !loading && row.items.length === 0) {
      // Loading finished with no items — collapse
      setCollapsed(true);
    }
    hadItemsRef.current = row.items.length > 0;
  }, [loading, row.items.length]);

  // What actually renders — forceExpanded overrides collapsed without
  // mutating it, so it's restored once forceExpanded lifts.
  const effectiveCollapsed = forceExpanded ? false : collapsed;

  const toggleCollapsed = () => {
    const next = !effectiveCollapsed;
    trackEvent(ANALYTICS_EVENTS.CONTENT_ROW_TOGGLE, { rowId: row.id, collapsed: next });
    setCollapsed(next);
  };

  return (
    <div className={styles.row}>
      <div className={styles.head}>
        <div className={styles.headLeft}>
          <div className={styles.titleRow}>
            <button
              className={styles.collapseBtn}
              onClick={toggleCollapsed}
              aria-expanded={!effectiveCollapsed}
              aria-label={effectiveCollapsed ? 'Expand section' : 'Collapse section'}
            >
              <svg
                className={`${styles.chevron} ${effectiveCollapsed ? styles.chevronCollapsed : ''}`}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {loading ? (
              // row.title/subtitle can still be stale placeholder data until
              // the real fetch resolves — skeleton them rather than showing
              // a name that doesn't match what's about to load in.
              <span className={styles.skeletonTitle} aria-hidden="true" />
            ) : (
              <>
                <span className={styles.title} dangerouslySetInnerHTML={{ __html: row.title }} />
                {row.subtitle && (
                  <span className={styles.subtitle}>· {row.subtitle}</span>
                )}
                {row.badge && (
                  <Badge variant={row.badgeVariant ?? 'green'}>{row.badge}</Badge>
                )}
              </>
            )}
            {onDelete && (
              <button
                className={styles.deleteCollectionBtn}
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                aria-label={`Delete collection ${row.title}`}
                title="Delete collection"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
                Delete
              </button>
            )}
          </div>
        </div>
        <div className={styles.headRight}>
          {row.items.length > 0 && (
            <button
              className={styles.viewAll}
              onClick={() => { trackEvent(ANALYTICS_EVENTS.CONTENT_ROW_VIEW_ALL, { rowId: row.id }); navigate(`/section/${row.id}`); }}
              aria-label={`View all ${row.title}`}
            >
              See all →
            </button>
          )}
        </div>
      </div>
      {!effectiveCollapsed && (
        loading
          ? <SkeletonShelf />
          : row.items.length === 0
            ? <EmptyShelf type={row.type} />
            : <div className={styles.shelf}>{children}</div>
      )}
    </div>
  );
}
